import type * as THREE from 'three'
import { Line2 } from 'three/examples/jsm/lines/Line2.js'
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js'
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js'
import type { GraphicsBackendKind } from '@/graphics/GraphicsConfig'

/**
 * 宽线（fat line）在双后端下的统一构造入口。
 *
 * WebGL2 使用 addons 的 `Line2` + `LineMaterial`；WebGPU 使用
 * `lines/webgpu/Line2` + `Line2NodeMaterial`。两者的渲染语义一致：屏幕像素线宽、
 * 世界单位虚线间距、depth test/write 可配；差异集中在材质类与「分辨率/虚线缩放」的
 * 写入方式，因此由本模块收敛，调用方只关心样式。
 */

/** WebGPU 侧宽线材质的最小结构化描述（避免静态依赖 three/webgpu）。 */
export interface WideLineNodeMaterialLike extends THREE.Material {
  linewidth: number
  scale: number
  dashed: boolean
  dashSize: number
  gapSize: number
  worldUnits: boolean
}

/** 宽线材质联合类型。 */
export type WideLineMaterial = WideLineMaterialLike | WideLineNodeMaterialLike

/** WebGL 侧 LineMaterial 的最小结构化描述。 */
export interface WideLineMaterialLike extends THREE.Material {
  linewidth: number
  dashScale: number
  dashed: boolean
  dashSize: number
  gapSize: number
  worldUnits: boolean
  resolution: THREE.Vector2
}

/** 宽线样式；单位与语义在双后端保持一致。 */
export interface WideLineStyle {
  readonly color: number
  /** 屏幕像素线宽。 */
  readonly linewidth: number
  readonly dashed: boolean
  /** 虚线段长度，单位与 dashScale 一致（本模块统一为像素）。 */
  readonly dashSize: number
  readonly gapSize: number
  readonly depthTest: boolean
  readonly depthWrite: boolean
  readonly transparent: boolean
}

/** 宽线对象：WebGL `Line2` 或 WebGPU `Line2`（两者都是 Mesh 子类）。 */
export type WideLineObject = THREE.Mesh & {
  computeLineDistances(): unknown
}

/** 宽线工厂：按 backend 选择材质与对象实现。 */
export interface WideLineFactory {
  readonly backend: GraphicsBackendKind
  /** 由折线点构造宽线对象，并计算虚线距离。 */
  createLine(points: readonly THREE.Vector3[], style: WideLineStyle): WideLineObject
  /** 判断材质是否属于当前 backend 的宽线材质。 */
  isWideLineMaterial(material: unknown): material is WideLineMaterial
  /** 写入 viewport 分辨率（WebGPU node 材质从 viewport 推导，无需写入）。 */
  applyViewportResolution(material: WideLineMaterial, width: number, height: number): void
  /** 写入虚线缩放：把世界单位距离换算成屏幕像素。 */
  applyDashScale(material: WideLineMaterial, pixelsPerWorld: number): void
}

/** 判断材质是否带 `dashScale`（WebGL LineMaterial）。 */
export function isWideLineMaterialLike(material: unknown): material is WideLineMaterialLike {
  return (
    typeof material === 'object' &&
    material !== null &&
    'dashScale' in material &&
    'resolution' in material &&
    'linewidth' in material
  )
}

/** 判断材质是否带 `scale`（WebGPU Line2NodeMaterial）。 */
export function isWideLineNodeMaterialLike(
  material: unknown
): material is WideLineNodeMaterialLike {
  return (
    typeof material === 'object' &&
    material !== null &&
    'isLine2NodeMaterial' in material &&
    'linewidth' in material
  )
}

interface WebGPUWideLineModule {
  readonly Line2: new (geometry: unknown, material: unknown) => WideLineObject
  readonly Line2NodeMaterial: new (parameters: Record<string, unknown>) => WideLineNodeMaterialLike
  readonly LineGeometry: new () => { setPositions(values: number[]): void }
}

let webgpuModulePromise: Promise<WebGPUWideLineModule> | null = null

/**
 * 惰性加载 WebGPU 宽线实现。
 *
 * `three/webgpu` 与 `lines/webgpu` 必须只在 WebGPU 运行时链路里加载，避免 WebGL 宿主
 * 为了宽线把整个 WebGPU 构建拖进产物。
 */
function loadWebGPUWideLineModule(): Promise<WebGPUWideLineModule> {
  if (!webgpuModulePromise) {
    webgpuModulePromise = (async () => {
      const [webgpu, line2Module, geometryModule] = await Promise.all([
        import('three/webgpu'),
        import('three/examples/jsm/lines/webgpu/Line2.js'),
        import('three/examples/jsm/lines/LineGeometry.js'),
      ])
      return {
        Line2: line2Module.Line2 as unknown as WebGPUWideLineModule['Line2'],
        Line2NodeMaterial:
          webgpu.Line2NodeMaterial as unknown as WebGPUWideLineModule['Line2NodeMaterial'],
        LineGeometry:
          geometryModule.LineGeometry as unknown as WebGPUWideLineModule['LineGeometry'],
      }
    })()
  }
  return webgpuModulePromise
}

class WebGLWideLineFactory implements WideLineFactory {
  readonly backend = 'webgl2' as const

  createLine(points: readonly THREE.Vector3[], style: WideLineStyle): WideLineObject {
    const material = new LineMaterial({
      color: style.color,
      linewidth: style.linewidth,
      dashed: style.dashed,
      dashSize: style.dashSize,
      gapSize: style.gapSize,
      worldUnits: false,
      depthTest: style.depthTest,
      depthWrite: style.depthWrite,
      transparent: style.transparent,
    })
    const geometry = new LineGeometry()
    geometry.setPositions(points.flatMap((point) => [point.x, point.y, point.z]))
    const line = new Line2(geometry, material) as unknown as WideLineObject
    line.computeLineDistances()
    return line
  }

  isWideLineMaterial(material: unknown): material is WideLineMaterial {
    return isWideLineMaterialLike(material)
  }

  applyViewportResolution(material: WideLineMaterial, width: number, height: number): void {
    if (isWideLineMaterialLike(material)) {
      material.resolution.set(width, height)
    }
  }

  applyDashScale(material: WideLineMaterial, pixelsPerWorld: number): void {
    if (isWideLineMaterialLike(material)) {
      material.dashScale = pixelsPerWorld
    }
  }
}

class WebGPUWideLineFactory implements WideLineFactory {
  readonly backend = 'webgpu' as const

  constructor(private readonly module: WebGPUWideLineModule) {}

  createLine(points: readonly THREE.Vector3[], style: WideLineStyle): WideLineObject {
    const material = new this.module.Line2NodeMaterial({
      color: style.color,
      linewidth: style.linewidth,
      dashed: style.dashed,
      dashSize: style.dashSize,
      gapSize: style.gapSize,
      worldUnits: false,
      depthTest: style.depthTest,
      depthWrite: style.depthWrite,
    })
    const geometry = new this.module.LineGeometry()
    geometry.setPositions(points.flatMap((point) => [point.x, point.y, point.z]))
    const line = new this.module.Line2(geometry, material)
    line.computeLineDistances()
    return line
  }

  isWideLineMaterial(material: unknown): material is WideLineMaterial {
    return isWideLineNodeMaterialLike(material)
  }

  /** WebGPU node 材质从当前 viewport 推导分辨率，不需要显式写入。 */
  applyViewportResolution(): void {}

  applyDashScale(material: WideLineMaterial, pixelsPerWorld: number): void {
    if (isWideLineNodeMaterialLike(material)) {
      material.scale = pixelsPerWorld
    }
  }
}

/** 创建与 backend 匹配的宽线工厂；WebGPU 需要先完成 `three/webgpu` 动态加载。 */
export async function createWideLineFactory(kind: GraphicsBackendKind): Promise<WideLineFactory> {
  if (kind === 'webgpu') {
    return new WebGPUWideLineFactory(await loadWebGPUWideLineModule())
  }
  return createWebGLWideLineFactory()
}

/**
 * 同步创建 WebGL2 宽线工厂。
 *
 * 供无需 preload 的纯 WebGL 场景（测试、默认分支）使用；WebGPU 必须走
 * `createWideLineFactory`，以便在渲染前完成模块加载。
 */
export function createWebGLWideLineFactory(): WideLineFactory {
  return new WebGLWideLineFactory()
}
